/*
HTTP Status Code
Informational responses (100–199)
Successful responses (200–299)
Redirects (300–399)
Client errors (400–499)
Server errors (500–599)
*/
import * as Constants from '@app/common/constants';
import env from '@app/env.json';
import {getAccessToken} from '@app/helper/login';
import {authTokenActions} from '@app/screens/generic/Login/redux';
import {store} from '../../App';
import {client} from './client';
import {appSliceActions} from '@app/store/reducers/appSlice';
import {ToasterService} from '@app/services';
import {ToastType} from '@app/services/toaster';
import {trackInsightTrace, generateUUID} from '@app/utils/applicationInsight';

let numberOfAjaxCAllPending = 0;

client.interceptors.request.use(
  config => {
    numberOfAjaxCAllPending = numberOfAjaxCAllPending + 1;
    store.dispatch(appSliceActions.setAppLoading(true));
    return config;
  },
  error => {
    numberOfAjaxCAllPending = numberOfAjaxCAllPending - 1;
    return Promise.reject(error);
  },
);

client.interceptors.response.use(response => {
  if (response.status === Constants.NOT_AUTHORIZED) {
    ToasterService.showToaster('Session Expired', ToastType.ERROR);
    store.dispatch(authTokenActions.signOut());
  }
  if (numberOfAjaxCAllPending > 0) {
    numberOfAjaxCAllPending = numberOfAjaxCAllPending - 1;
  }
  if (numberOfAjaxCAllPending <= 0) {
    store.dispatch(appSliceActions.setAppLoading(false));
  }

  return response;
});

const getNetworkResponse = async (config: any, apiPath: any) => {
  return client(config)
    .then(async function (response: any) {
      const trackObj = {
        url: config.url,
        response: response,
        type: config.method,
        CorrelationId: config.headers.CorrelationId,
      };
      if (config.method === 'POST') {
        trackInsightTrace(`Post call for api ${config.url}`, {
          ...trackObj,
          payload: config.data,
        });
      } else {
        trackInsightTrace(`Get call for api ${config.url}`, trackObj);
      }
      return response;
    })
    .catch(async (error: any) => {
      trackInsightTrace(`${config.method} api error for ${config.url}`, {
        error: error,
        payload: config.method === 'POST' ? config.data : null,
        CorrelationId: config.headers.CorrelationId,
      });
      if (error?.response?.status === Constants.NOT_AUTHORIZED) {
        store.dispatch(authTokenActions.signOut());
      }
      if (numberOfAjaxCAllPending > 0) {
        numberOfAjaxCAllPending = numberOfAjaxCAllPending - 1;
      }
      if (numberOfAjaxCAllPending <= 0) {
        store.dispatch(appSliceActions.setAppLoading(false));
      }
      return error.response || error.message || apiPath;
    });
};

/*
Function to handle HTTP GET request
@params- for query params
*/
export const get = async (url: any, params: any, apiPath: any, fetchOnline: boolean = false,
  responseType: any = null,) => {
  const accessToken = await getAccessToken();

  const config: any = {
    baseURL: env.API_HOST,
    method: 'GET',
    url,
    headers: {
      Authorization: `Bearer ${accessToken}`,
      CorrelationId: generateUUID(),
    },
    params,
  };
  // Add responseType if specified
  if (responseType) {
    config.responseType = responseType;
  }
  if (accessToken) {
    return getNetworkResponse(config, apiPath);
  }
};

/*
Function to handle HTTP POST request
@data for passing data as body
@params- for query params
*/
export const post = async (
  url: any,
  data: any = {},
  params: any = {},
  apiPath: any = null,
  responseType: any = null // <-- add this
) => {
  const accessToken = await getAccessToken();
  const config: any = {
    baseURL: env.API_HOST,
    method: 'POST',
    url,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
      CorrelationId: generateUUID(),
    },
    data,
    params,
  };
  if (responseType) {
    config.responseType = responseType; 
  }
  if (accessToken) {
    return getNetworkResponse(config, apiPath);
  }
};

export const postAgenticExpense = async (
  url: any,
  data: any = {},
  params: any = {},
  apiPath: any = null,
  responseType: any = null // <-- add this
) => {
  const accessToken = await getAccessToken();
  const config: any = {
    baseURL: env.API_HOST_AGENTIC_EXPENSE,
    method: 'POST',
    url,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
      CorrelationId: generateUUID(),
    },
    data,
    params,
  };
  if (responseType) {
    config.responseType = responseType; 
  }
  if (accessToken) {
    return getNetworkResponse(config, apiPath);
  }
};

export const getAgenticExpense = async (url: any, params: any, apiPath: any) => {
  const accessToken = await getAccessToken();

  const config = {
    baseURL: env.API_HOST_AGENTIC_EXPENSE,
    method: 'GET',
    url,
    headers: {
      Authorization: `Bearer ${accessToken}`,
      CorrelationId: generateUUID(),
    },
    params,
  };

  if (accessToken) {
    return getNetworkResponse(config, apiPath);
  }
};

export const postUploadFile = async (
  url: any,
  data: any = {},
  params: any = {},
  apiPath: any = null,
) => {
  const accessToken = await getAccessToken();
  const config = {
    baseURL: env.API_HOST,
    method: 'POST',
    url,
    headers: {
      'Content-Type':
        'multipart/form-data;boundary=----WebKitFormBoundaryVaou4bB7hlUIjuOY',
      Authorization: `Bearer ${accessToken}`,
      CorrelationId: generateUUID(),
    },
    data,
    params,
  };
  if (accessToken) {
    return getNetworkResponse(config, apiPath);
  }
};

/**
 * Handle HTTP POST request for multipart form data
 * @param {string} url http url of api
 * @param {FormData} data data to pass in body
 * @param {object} params params to pass in api call
 * @param {object} apiPath api path for tracing
 */
export const postMultipartFormData = async (
  url: any,
  data: FormData,
  params: any = {},
  apiPath: any = null,
) => {
  const accessToken = await getAccessToken();
  const config = {
    baseURL: env.API_HOST,
    method: 'POST',
    url,
    headers: {
      Authorization: `Bearer ${accessToken}`,
      CorrelationId: generateUUID(),
    },
    data,
    params,
  };
  if (accessToken) {
    return getNetworkResponse(config, apiPath);
  }
};

/**
 * Handle HTTP PUT request for multipart uploads
 * @param {string} url http url of api
 * @param {object} data data to pass in body
 * @param {object} params params to pass in api call
 * @param {object} apiPath api path for tracing
 */
export const putUploadFile = async (
  url: any,
  data: any = {},
  params: any = {},
  apiPath: any = null,
) => {
  const accessToken = await getAccessToken();
  const config = {
    baseURL: env.API_HOST,
    method: 'PUT',
    url,
    headers: {
      'Content-Type': 'multipart/form-data',
      Authorization: `Bearer ${accessToken}`,
      CorrelationId: generateUUID(),
    },
    data,
    params,
  };
  if (accessToken) {
    return getNetworkResponse(config, apiPath);
  }
};

/**
 * Handle HTTP PUT request for multipart form data
 * @param {string} url http url of api
 * @param {FormData} data data to pass in body
 * @param {object} params params to pass in api call
 * @param {object} apiPath api path for tracing
 */
export const putMultipartFormData = async (
  url: any,
  data: FormData,
  params: any = {},
  apiPath: any = null,
) => {
  const accessToken = await getAccessToken();
  const config = {
    baseURL: env.API_HOST,
    method: 'PUT',
    url,
    headers: {
      Authorization: `Bearer ${accessToken}`,
      CorrelationId: generateUUID(),
    },
    data,
    params,
  };
  if (accessToken) {
    return getNetworkResponse(config, apiPath);
  }
};

/**
 * Handle HTTP PUT request
 * @param {string} url http url of api
 * @param {object} data data to pass in body
 * @param {object} params params to pass in api call
 */
export const put = async (
  url: any,
  data: any = {},
  params: any = {},
  apiPath: any = null,
) => {
  const accessToken = await getAccessToken();
  const config = {
    baseURL: env.API_HOST,
    method: 'PUT',
    url,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
      CorrelationId: generateUUID(),
    },
    data,
    params,
  };

  if (accessToken) {
    return getNetworkResponse(config, apiPath);
  }
};

/**
 * Handle HTTP DELETE request
 * @param {string} url http url of api
 * @param {object} data data to pass in body
 * @param {object} params params to pass in api call
 */
export const Delete = async (
  url: any,
  data: any = {},
  params: any = {},
  apiPath: any = null,
) => {
  const accessToken = await getAccessToken();
  const config = {
    baseURL: env.API_HOST,
    method: 'DELETE',
    url,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
      CorrelationId: generateUUID(),
    },
    data,
    params,
  };

  if (accessToken) {
    return getNetworkResponse(config, apiPath);
  }
};

/*
Function to handle HTTP GET request
@params- for query params
*/
export const getOpen = async (url: any, params: any, apiPath: any) => {
  const config = {
    baseURL: env.API_HOST,
    method: 'GET',
    url,
    params,
  };

  return getNetworkResponse(config, apiPath);
};

const NetworkService = {
  get,
  post,
  put,
  Delete,
  getOpen,
  postUploadFile,
  postAgenticExpense,
  getAgenticExpense,
  putUploadFile,
  putMultipartFormData,
  postMultipartFormData,
};

export default NetworkService;
